import { TableFilter } from "@neonumy/ant-design";
import { useGetWindowHeight } from "@neonumy/neonumy-ui";
import { Space, Typography } from "antd";
import { Content } from "antd/es/layout/layout";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import CreateUpdateComment from "./components/CreateUpdateComment";
import RemoveButton from "./components/RemoveButton";
import useCommentsApi from "./hooks/useCommentsApi";
import { sanitizeHtml } from "utils/sanitizeHtml";

export default function Comments(): React.ReactElement {
  const windowHeight = useGetWindowHeight();
  const urlParams: RouteParams = useParams();
  const FILTER_INIT = {
    search: undefined,
    companyId: urlParams.companyId,
  };
  const paginationInit = {
    offset: 0,
    limit: 10,
  };

  const { t } = useTranslation();
  const [filters, setFilters] = useState(FILTER_INIT);

  const { refetch, loading, dataSource, listComments, totalCount } = useCommentsApi();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const COLUMNS = [
    {
      title: t("Name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("Reference"),
      dataIndex: "reference",
      key: "reference",
    },
    {
      title: t("Description"),
      dataIndex: "description",
      key: "description",
      render: (_text: unknown, record: ListCommentsData) => {
        return <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(record?.description) }} />;
      },
      ellipsis: true,
    },
    {
      dataIndex: t("Action"),
      key: "action",
      title: "Action",
      align: "center",
      width: "100px",
      render: (_text: unknown, record: ListCommentsData) => {
        return (
          <Space size="small" direction="horizontal" className="centered">
            <CreateUpdateComment id={record.id} refetch={refetch} />
            <RemoveButton id={record.id} refetch={refetch} />
          </Space>
        );
      },
    },
  ];

  useEffect(() => {
    if (urlParams.companyId) {
      listComments({
        variables: {
          filter: filters,
          ...paginationInit,
        },
      });
    }
  }, [urlParams.companyId]);

  const handleSearchFilters = (confirm: () => void) => {
    confirm();

    if (urlParams.companyId) {
      listComments({ variables: { filter: { ...filters }, ...paginationInit } });
    }
  };

  const handleResetFilter = (_filterList: unknown, confirm: () => void) => {
    confirm();
    listComments({ variables: { filter: { ...FILTER_INIT }, ...paginationInit } });
  };

  const handlePagination = (page: number, size: number) => {
    setPageSize(size);
    setCurrentPage(page);
    refetch({
      limit: size,
      offset: size * (page - 1),
    });
  };

  return (
    <Content className="layout-card-content">
      <Typography.Title className="documents-title" level={4}>
        {t("Comments")}
      </Typography.Title>
      <TableFilter
        className="antd-newrai-table"
        scroll={{ y: windowHeight - 350 }}
        dataSource={dataSource}
        loading={loading}
        columns={COLUMNS}
        defaultSearchFilter={handleSearchFilters}
        defaultResetFilter={handleResetFilter}
        defaultFiltersObject={filters}
        defaultFilterSet={setFilters}
        pagination={{
          onChange: handlePagination,
          showSizeChanger: true,
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showTotal: total => t("Total table items", { count: total }),
        }}
        debounced={true}
      />

      <CreateUpdateComment refetch={refetch} />
    </Content>
  );
}
